import { Link2, UserRound } from "lucide-react"

import { Insignia } from "@/componentes/ui/Insignia"

import { ACENTO, iniciales, type Nodo } from "./tipos"

/**
 * Una posición del organigrama.
 *
 * ⚠ **La vacante se dice con la palabra, no con un hueco.** Una caja con el nombre en
 * blanco se lee como un error de datos; «Vacante» se lee como lo que es, un puesto que
 * hay que cubrir. Por eso también el título de la tarjeta es el CARGO y no la persona:
 * el organigrama describe la estructura, y la estructura existe aunque nadie la ocupe.
 *
 * ⚠ **El badge de reporte matricial va en la tarjeta, no sólo en la línea.** Las
 * líneas punteadas se cruzan y en un mapa denso no se sigue cuál sale de dónde; el
 * badge dice «además reporta a alguien» sin tener que seguir ninguna línea.
 */

export function TarjetaPosicion({
  nodo,
  seleccionada,
  arrastrable,
  alElegir,
  alSoltar,
}: {
  nodo: Nodo
  seleccionada: boolean
  arrastrable: boolean
  alElegir: (id: number) => void
  alSoltar?: (arrastrado: number, destino: number) => void
}) {
  const acento = ACENTO[nodo.nivel ?? "POSICION"] ?? ACENTO.POSICION

  return (
    <button
      type="button"
      onClick={() => alElegir(nodo.id)}
      aria-current={seleccionada ? "true" : undefined}
      draggable={arrastrable}
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", String(nodo.id))
        e.dataTransfer.effectAllowed = "move"
      }}
      onDragOver={(e) => {
        if (alSoltar) e.preventDefault()
      }}
      onDrop={(e) => {
        e.preventDefault()
        const arrastrado = Number(e.dataTransfer.getData("text/plain"))
        if (alSoltar && arrastrado && arrastrado !== nodo.id) alSoltar(arrastrado, nodo.id)
      }}
      className="w-[190px] rounded-[var(--radius-tarjeta)] border px-3 py-2 text-left"
      style={{
        borderColor: seleccionada ? acento : "var(--border)",
        borderLeftWidth: "3px",
        borderLeftColor: acento,
        backgroundColor: seleccionada ? "var(--surface-elevated)" : "var(--surface)",
      }}
    >
      <div className="flex items-center gap-2">
        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold"
          style={{
            backgroundColor: "var(--surface-elevated)",
            color: "var(--fg-muted)",
            border: nodo.vacante ? "1px dashed var(--border)" : undefined,
          }}
        >
          {nodo.foto ? (
            <img
              src={nodo.foto}
              alt=""
              className="h-8 w-8 rounded-full object-cover"
              loading="lazy"
            />
          ) : nodo.vacante ? (
            <UserRound size={14} aria-hidden="true" />
          ) : (
            iniciales(nodo.ocupante)
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold">
            {nodo.cargo ?? "(sin cargo)"}
          </span>
          <span className="block truncate text-xs" style={{ color: "var(--fg-muted)" }}>
            {nodo.vacante ? "Vacante" : nodo.ocupante}
          </span>
        </span>
      </div>
      {(nodo.departamento || nodo.reporta_tambien_a.length > 0) && (
        <div className="mt-2 flex flex-wrap items-center gap-1">
          {nodo.departamento && <Insignia tono="neutro">{nodo.departamento}</Insignia>}
          {nodo.reporta_tambien_a.length > 0 && (
            <Insignia tono="info">
              <Link2 size={11} aria-hidden="true" /> {nodo.reporta_tambien_a.length}
            </Insignia>
          )}
        </div>
      )}
    </button>
  )
}
